import { useState, useEffect } from 'react'
import { useRecords, useRecordPick, useRecordParlay, useRecordFuturesPick } from '../hooks/useRecords'
import UserProfileModal from '../components/profile/UserProfileModal'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import EmptyState from '../components/ui/EmptyState'
import ErrorState from '../components/ui/ErrorState'
import TierBadge from '../components/ui/TierBadge'
import Avatar from '../components/ui/Avatar'
import GameCard from '../components/picks/GameCard'
import ParlayCard from '../components/picks/ParlayCard'
import FuturesPickCard from '../components/picks/FuturesPickCard'
import { formatRecordValue } from '../lib/recordFormat'

const CATEGORY_ORDER = ['streak', 'single_pick', 'percentage', 'efficiency', 'climb']

// Categories rendered as side-by-side pairs on desktop (single-column on mobile).
// Efficiency and Climb each have only 1-4 short records, so pairing them makes
// good use of the wider desktop layout.
const PAIRED_ROWS = [['efficiency', 'climb']]

// Display order within each category (lower = higher on list)
const RECORD_SORT = {
  longest_win_streak: 0,
  longest_parlay_streak: 1,
  longest_prop_streak: 2,
  longest_survivor_streak: 3,
  longest_crown_tenure: 4,
  biggest_underdog_hit: 0,
  biggest_parlay: 1,
  most_parlay_legs: 2,
  best_futures_hit: 3,
  highest_prop_pct: 0,
  biggest_dog_lover: 1,
  highest_overall_win_pct: 2,
  fewest_picks_to_baller: 0,
  fewest_picks_to_elite: 1,
  fewest_picks_to_hof: 2,
  fewest_picks_to_goat: 3,
  great_climb: 0,
}
const CATEGORY_LABELS = {
  streak: 'Streaks',
  single_pick: 'Big Hits',
  percentage: 'Percentages',
  efficiency: 'Efficiency',
  climb: 'Climb',
}

// Records that can show a detail card when tapped
const TAPPABLE_PICK_RECORDS = ['biggest_underdog_hit']
const TAPPABLE_PARLAY_RECORDS = ['biggest_parlay', 'most_parlay_legs']
const TAPPABLE_FUTURES_RECORDS = ['best_futures_hit']

function hasDetailCard(recordKey) {
  return TAPPABLE_PICK_RECORDS.includes(recordKey) || TAPPABLE_PARLAY_RECORDS.includes(recordKey) || TAPPABLE_FUTURES_RECORDS.includes(recordKey)
}

function PickDetailCard({ pickId }) {
  const { data: pick, isLoading } = useRecordPick(pickId)
  if (isLoading) return <div className="px-4 pb-4"><LoadingSpinner /></div>
  if (!pick?.games) return null
  return (
    <div className="px-4 pb-4">
      <GameCard game={pick.games} userPick={pick} />
    </div>
  )
}

function ParlayDetailCard({ parlayId }) {
  const { data: parlay, isLoading } = useRecordParlay(parlayId)
  if (isLoading) return <div className="px-4 pb-4"><LoadingSpinner /></div>
  if (!parlay) return null
  return (
    <div className="px-4 pb-4">
      <ParlayCard parlay={parlay} />
    </div>
  )
}

function FuturesDetailCard({ pickId }) {
  const { data: pick, isLoading } = useRecordFuturesPick(pickId)
  if (isLoading) return <div className="px-4 pb-4"><LoadingSpinner /></div>
  if (!pick) return null
  return (
    <div className="px-4 pb-4">
      <FuturesPickCard pick={pick} />
    </div>
  )
}

function RecordCard({ record, onUserTap, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const [showDetail, setShowDetail] = useState(false)
  const holder = record.users
  const hasSubs = record.sub_records?.length > 0
  const hasHolder = holder != null
  const isTappable = hasHolder && hasDetailCard(record.record_key)
  const metadata = record.record_metadata || {}

  const handleCardClick = () => {
    if (hasSubs) {
      setExpanded(!expanded)
    } else if (isTappable) {
      setShowDetail(!showDetail)
    }
  }

  return (
    <div id={`record-${record.record_key}`} className="bg-bg-primary border border-text-primary/20 rounded-2xl overflow-hidden">
      <div
        className={`p-4 ${hasSubs || isTappable ? 'cursor-pointer' : ''}`}
        onClick={handleCardClick}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-text-primary">{record.display_name}</h3>
              {hasSubs && (
                <svg
                  width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  className={`text-text-muted transition-transform ${expanded ? 'rotate-180' : ''}`}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              )}
              {isTappable && !hasSubs && (
                <svg
                  width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  className={`text-text-muted transition-transform ${showDetail ? 'rotate-180' : ''}`}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              )}
            </div>
            <p className="text-xs text-text-muted">{record.description}</p>
          </div>

          {hasHolder && (
            <div className="text-right flex-shrink-0">
              <div className="text-xl font-bold text-white">{formatRecordValue(record)}</div>
            </div>
          )}
        </div>

        {hasHolder && (
          <div
            className="flex items-center gap-2 mt-3 pt-3 border-t border-border cursor-pointer hover:opacity-80 transition-opacity"
            onClick={(e) => { e.stopPropagation(); onUserTap?.(holder.id) }}
          >
            <Avatar user={holder} size="xl" className="bg-accent/20" />
            <span className="text-base font-medium text-text-primary">{holder.display_name || holder.username}</span>
            <TierBadge tier={holder.tier} size="xs" />
          </div>
        )}

        {!hasHolder && (
          <div className="mt-3 pt-3 border-t border-border">
            <span className="text-sm text-text-muted italic">No record holder yet</span>
          </div>
        )}
      </div>

      {/* Detail card for tappable records */}
      {showDetail && TAPPABLE_PICK_RECORDS.includes(record.record_key) && metadata.pickId && (
        <div className="border-t border-border">
          <PickDetailCard pickId={metadata.pickId} />
        </div>
      )}
      {showDetail && TAPPABLE_PARLAY_RECORDS.includes(record.record_key) && metadata.parlayId && (
        <div className="border-t border-border">
          <ParlayDetailCard parlayId={metadata.parlayId} />
        </div>
      )}
      {showDetail && TAPPABLE_FUTURES_RECORDS.includes(record.record_key) && metadata.futuresPickId && (
        <div className="border-t border-border">
          <FuturesDetailCard pickId={metadata.futuresPickId} />
        </div>
      )}

      {expanded && hasSubs && (
        <div className="border-t border-border">
          {record.record_key === 'longest_win_streak' && (
            <div className="px-4 py-2 flex items-center gap-1.5 text-[10px] text-text-muted border-b border-border">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              <span>Sport-specific streaks may include losses in other sports during the streak period</span>
            </div>
          )}
          {record.sub_records
            .filter((s) => s.record_holder_id != null)
            .map((sub) => (
              <div key={sub.record_key} className="px-4 py-3 border-b border-border last:border-b-0 bg-bg-secondary/50">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-text-primary">{sub.display_name}</div>
                    {/* The holder is the point of a record row, so the face
                        and the name are sized to be read rather than squinted
                        at. xs (20px) and text-xs put the person who set the
                        record below the explanatory copy around it in visual
                        weight.

                        Still a step under the parent row above, which keeps
                        xl and text-base plus a tier badge, so the headline
                        record stays the headline. */}
                    {sub.users && (
                      <div
                        className="flex items-center gap-2 mt-1.5 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => onUserTap?.(sub.users.id)}
                      >
                        <Avatar user={sub.users} size="lg" className="bg-accent/20" />
                        <span className="text-sm text-text-secondary">{sub.users.display_name || sub.users.username}</span>
                      </div>
                    )}
                  </div>
                  <div className="text-lg font-bold text-white">{formatRecordValue(sub)}</div>
                </div>
              </div>
            ))}
          {record.sub_records.filter((s) => s.record_holder_id != null).length === 0 && (
            <div className="px-4 py-3 text-sm text-text-muted italic">No sport records set yet</div>
          )}
        </div>
      )}
    </div>
  )
}

export function RecordBookContent({ scrollToRecord = null }) {
  const { data: records, isLoading, isError, refetch } = useRecords()
  const [profileUserId, setProfileUserId] = useState(null)

  useEffect(() => {
    if (scrollToRecord && records) {
      setTimeout(() => {
        const el = document.getElementById(`record-${scrollToRecord}`)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 300)
    }
  }, [scrollToRecord, records])

  // Group records by category — only show records that have a holder
  const grouped = {}
  for (const cat of CATEGORY_ORDER) {
    grouped[cat] = []
  }
  for (const record of records || []) {
    if (grouped[record.category] && record.record_holder_id) {
      // Also filter sub-records to only those with holders
      const filtered = { ...record, sub_records: (record.sub_records || []).filter((s) => s.record_holder_id) }
      grouped[record.category].push(filtered)
    }
  }
  // Sort records within each category by defined display order
  for (const cat of CATEGORY_ORDER) {
    grouped[cat].sort((a, b) => (RECORD_SORT[a.record_key] ?? 99) - (RECORD_SORT[b.record_key] ?? 99))
  }

  if (isLoading) return <LoadingSpinner />
  if (isError) return <ErrorState title="Failed to load records" message="Check your connection and try again." onRetry={refetch} />
  if (!records?.length) return <EmptyState title="No records yet" message="Records will appear as picks are settled." />

  // Render one category block (header + featured record + 2-col grid)
  function renderCategory(cat) {
    const catRecords = grouped[cat]
    if (!catRecords?.length) return null

    // Streaks category: the all-sport win streak has per-sport sub-records
    // that expand. Give it its own full-width row (expanded by default),
    // then lay the remaining 4 records out as 2x2 underneath.
    const isStreakCategory = cat === 'streak'
    const featuredRecord = isStreakCategory ? catRecords[0] : null
    const gridRecords = isStreakCategory ? catRecords.slice(1) : catRecords

    // Split records into two independent flex-columns so expanding one
    // card doesn't force its row-mate to grow (CSS grid shares row height).
    const mid = Math.ceil(gridRecords.length / 2)
    const leftCol = gridRecords.slice(0, mid)
    const rightCol = gridRecords.slice(mid)

    return (
      <div key={cat}>
        <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-3">
          {CATEGORY_LABELS[cat]}
        </h2>

        {featuredRecord && (
          <div className="mb-3">
            <RecordCard
              record={featuredRecord}
              onUserTap={setProfileUserId}
              defaultExpanded
            />
          </div>
        )}

        <div className="lg:flex lg:gap-3 lg:items-start">
          <div className="space-y-3 lg:flex-1 lg:min-w-0">
            {leftCol.map((record) => (
              <RecordCard key={record.record_key} record={record} onUserTap={setProfileUserId} />
            ))}
          </div>
          {rightCol.length > 0 && (
            <div className="space-y-3 mt-3 lg:mt-0 lg:flex-1 lg:min-w-0">
              {rightCol.map((record) => (
                <RecordCard key={record.record_key} record={record} onUserTap={setProfileUserId} />
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  // Build the final row list: paired categories become one row with two
  // side-by-side blocks on desktop; all others are single-block rows.
  const pairedSet = new Set(PAIRED_ROWS.flat())
  const rows = []
  for (const cat of CATEGORY_ORDER) {
    if (pairedSet.has(cat)) continue
    rows.push([cat])
  }
  for (const pair of PAIRED_ROWS) {
    const visible = pair.filter((c) => grouped[c]?.length)
    if (visible.length) rows.push(visible)
  }

  return (
    <>
      <div className="space-y-8">
        {rows.map((row, rowIdx) => {
          if (row.length === 1) return renderCategory(row[0])
          // Multi-category row — side-by-side on desktop, stacked on mobile
          return (
            <div key={`row-${rowIdx}`} className="lg:grid lg:grid-cols-2 lg:gap-6 space-y-8 lg:space-y-0">
              {row.map((cat) => (
                <div key={cat}>{renderCategory(cat)}</div>
              ))}
            </div>
          )
        })}
      </div>
      <UserProfileModal userId={profileUserId} onClose={() => setProfileUserId(null)} />
    </>
  )
}

export default function RecordBookPage() {
  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <h1 className="font-display text-3xl mb-2">IKB Official Record Book</h1>
      <p className="text-sm text-text-muted mb-6">All-time records across I KNOW BALL</p>
      <RecordBookContent />
    </div>
  )
}
